import React from 'react';
import {Avatar, Box, Chip, Collapse, Divider, IconButton, Slider, Stack, Tooltip, Typography} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
    collectChartFacetChips,
    groupFacetChips,
    isFacetChipSelected,
    toggleFacetChip,
} from '../utils/chartFacetChips';
import {
    createDefaultEntityFilters,
    isEntityFilterActive,
} from '../utils/evaluateEntityPropertyFilters';
import {getEntityTypeMeta} from '../../../utils/entityTypeMeta';
import {
    DEFAULT_MAX_DISTANCE,
    MAX_MAX_DISTANCE,
    MIN_MAX_DISTANCE,
} from '../utils/expandEntityIdsByChartDistance';

/**
 * Chart facet filter — split into two render modes:
 *
 *  toolbarOnly=true  → compact inline toolbar (entity-type chips + attributes
 *                       toggle + slider) intended to live inside WhiteboardHeader.
 *                       Does NOT render the collapsible chip panel.
 *
 *  toolbarOnly=false (default) → renders only the collapsible Attributes chip
 *                       panel (Zone 3 chips + Zone 1 active-filters banner).
 *                       Place this directly below the header.
 *
 * Both instances should receive the same props; they share attributesOpen state
 * via the onAttributesOpenChange / attributesOpen props lifted to WhiteboardPage.
 */
export default function ChartFacetChipBar({
    entityFilters = [],
    onEntityFiltersChange,
    chartEntities = [],
    propertiesByEntityId = {},
    summariesByEntityId = {},
    propertyOptions = [],
    summariesLoading = false,
    maxDistance = DEFAULT_MAX_DISTANCE,
    onMaxDistanceChange,
    // Split-mode props
    toolbarOnly = false,
    attributesOpen = false,
    onAttributesOpenChange,
}) {
    const [hoveredGroup, setHoveredGroup] = React.useState(null);

    const chips = React.useMemo(
        () => collectChartFacetChips({
            chartEntities,
            propertiesByEntityId,
            summariesByEntityId,
            filters: entityFilters,
            propertyOptions,
            summariesLoading,
        }),
        [chartEntities, propertiesByEntityId, summariesByEntityId, entityFilters, propertyOptions, summariesLoading],
    );

    const groups = React.useMemo(() => groupFacetChips(chips), [chips]);
    const entityTypeGroup = groups.find((g) => g.groupLabel === 'Entity type');
    const propertyGroups = groups.filter((g) => g.groupLabel !== 'Entity type');

    const selectedChips = React.useMemo(
        () => chips.filter((chip) => isFacetChipSelected(entityFilters, chip)),
        [chips, entityFilters],
    );

    const filtersActive = isEntityFilterActive(entityFilters);

    const handleChipClick = React.useCallback((chip) => {
        onEntityFiltersChange?.(toggleFacetChip(entityFilters, chip, {
            chartEntities,
            propertiesByEntityId,
            summariesByEntityId,
            summariesLoading,
        }));
    }, [entityFilters, onEntityFiltersChange, chartEntities, propertiesByEntityId, summariesByEntityId, summariesLoading]);

    const handleClear = React.useCallback(() => {
        onEntityFiltersChange?.(createDefaultEntityFilters());
    }, [onEntityFiltersChange]);

    const chipSx = {fontSize: 11, height: 22, cursor: 'pointer'};

    // ── TOOLBAR MODE ─────────────────────────────────────────────────────────
    // Renders entity-type chips and active-filter chips inline; optionally also
    // the hops slider (only when property groups + onMaxDistanceChange present).
    if (toolbarOnly) {
        const hasSlider = propertyGroups.length > 0 && Boolean(onMaxDistanceChange);
        // Nothing to render at all — bail early only when there are no entity-type
        // chips AND no active filters AND no slider.
        if (!entityTypeGroup && selectedChips.length === 0 && !filtersActive && !hasSlider) return null;
        return (
            <Stack
                data-testid="chart-facet-chip-bar"
                direction="row"
                alignItems="center"
                spacing={0}
                sx={{minWidth: 0, flexShrink: 0, height: '100%', overflow: 'visible'}}
            >
                {/* Entity type chips */}
                {entityTypeGroup && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{mr: 1}}/>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            data-testid="chart-facet-entity-type-row"
                        >
                            {entityTypeGroup.chips.map((chip) => {
                                const selected = isFacetChipSelected(entityFilters, chip);
                                const meta = getEntityTypeMeta(chip.token);
                                const dotColor = meta.color || '#9e9e9e';
                                return (
                                    <Chip
                                        key={chip.id}
                                        data-testid={`chart-facet-chip-${chip.id}`}
                                        label={
                                            <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                                                <Box component="span" sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0, display: 'inline-block'}}/>
                                                {chip.label}
                                                <span style={{fontSize: 11, fontWeight: 700, lineHeight: 1, opacity: selected ? 0.75 : 0.5}}>
                                                    {chip.count}
                                                </span>
                                            </span>
                                        }
                                        variant={selected ? 'filled' : 'outlined'}
                                        color={selected ? 'primary' : 'default'}
                                        onClick={() => handleChipClick(chip)}
                                        aria-pressed={selected}
                                        sx={chipSx}
                                    />
                                );
                            })}
                        </Stack>
                    </>
                )}

                {/* Active-filter summary chips (Zone 1) */}
                {selectedChips.length > 0 && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{mx: 1}}/>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {selectedChips.map((chip) => (
                                <Chip
                                    key={chip.id}
                                    data-testid={`chart-facet-chip-selected-${chip.id}`}
                                    label={chip.label}
                                    size="small"
                                    variant="filled"
                                    color="primary"
                                    onDelete={() => handleChipClick(chip)}
                                    sx={chipSx}
                                />
                            ))}
                        </Stack>
                    </>
                )}

                {/* Hops slider — only when property groups and handler are present */}
                {hasSlider && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{mr: 1}}/>
                        <Tooltip
                            title="Controls how many graph hops away from a filter match are also kept visible — 0 = exact matches only, 1 = matches + direct neighbors, 2 = + neighbors of neighbors, etc."
                            placement="bottom"
                            enterDelay={300}
                            arrow
                        >
                            <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0, cursor: 'default'}}
                            >
                                Reach
                            </Typography>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{mx: 0.75, height: 14, alignSelf: 'center'}}/>
                        <Tooltip
                            title="Show entities within N hops of a filter match. 0 = exact matches only; 1 (default) = matches + direct neighbors."
                            placement="bottom"
                            enterDelay={400}
                            arrow
                        >
                            <Stack direction="row" spacing={0.3} alignItems="center" sx={{cursor: 'default', mr: 0.5}}>
                                <HubOutlinedIcon sx={{fontSize: 13, color: 'text.disabled'}}/>
                                <Typography variant="caption" color="text.disabled" sx={{fontSize: 10, fontWeight: 600}}>
                                    {maxDistance === 0 ? '0' : `+${maxDistance}`}
                                </Typography>
                            </Stack>
                        </Tooltip>
                        <Slider
                            data-testid="chart-facet-distance-slider"
                            size="small"
                            min={MIN_MAX_DISTANCE}
                            max={MAX_MAX_DISTANCE}
                            step={1}
                            value={maxDistance}
                            onChange={(_e, val) => onMaxDistanceChange(val)}
                            marks
                            valueLabelDisplay="off"
                            aria-label="Max distance"
                            sx={{width: 80, flexShrink: 0}}
                        />
                    </>
                )}

                {/* Clear button — any time filters are active */}
                {filtersActive && (
                    <>
                        <Divider orientation="vertical" flexItem sx={{mx: 1}}/>
                        <Tooltip title="Clear all filters" placement="bottom" enterDelay={400}>
                            <IconButton
                                data-testid="chart-facet-chip-clear"
                                size="small"
                                aria-label="Clear filters"
                                onClick={handleClear}
                            >
                                <ClearIcon sx={{fontSize: 16}}/>
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Stack>
        );
    }

    // ── CHIPS PANEL MODE ─────────────────────────────────────────────────────
    // Renders only the collapsible attribute chip groups below the header.
    return (
        <Box data-testid="chart-facet-chip-bar" sx={{borderBottom: attributesOpen ? 1 : 0, borderColor: 'divider', bgcolor: 'background.paper'}}>
            {/* Always-visible header row with collapse toggle */}
            <Stack
                direction="row"
                alignItems="center"
                sx={{px: 1.5, pt: 0.75, pb: attributesOpen ? 0 : 0.75, gap: 1, flexWrap: 'wrap', useFlexGap: true}}
            >
                {/* Collapse toggle + label */}
                <Tooltip title={attributesOpen ? 'Collapse filter panel' : 'Expand filter panel'} placement="bottom" enterDelay={400}>
                    <Stack
                        component="button"
                        direction="row"
                        alignItems="center"
                        spacing={0.25}
                        onClick={() => onAttributesOpenChange?.(!attributesOpen)}
                        sx={{
                            all: 'unset',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '2px',
                            flexShrink: 0,
                            mr: 0.5,
                            borderRadius: 1,
                            px: 0.5,
                            py: 0.25,
                            '&:hover': {bgcolor: 'action.hover'},
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{fontWeight: 700, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase'}}
                        >
                            Filter
                        </Typography>
                        {attributesOpen
                            ? <ExpandLessIcon sx={{fontSize: 14, color: 'text.disabled'}}/>
                            : <ExpandMoreIcon sx={{fontSize: 14, color: 'text.disabled'}}/>
                        }
                    </Stack>
                </Tooltip>
            </Stack>

            {/* Collapsible chips */}
            <Collapse in={attributesOpen} unmountOnExit>
                <Stack
                    spacing={0}
                    data-testid="chart-facet-all-row"
                >
                    <Stack
                        direction="row"
                        spacing={0}
                        alignItems="flex-start"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{px: 1.5, pb: 0.75, gap: 1}}
                    >
                        {/* Entity type group — first, separated by a thicker divider from properties */}
                        {entityTypeGroup && (
                            <Box
                                onMouseEnter={() => setHoveredGroup('__entity_type__')}
                                onMouseLeave={() => setHoveredGroup(null)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: hoveredGroup === '__entity_type__' ? 'divider' : 'transparent',
                                    bgcolor: hoveredGroup === '__entity_type__' ? 'action.hover' : 'transparent',
                                    transition: 'background-color 120ms ease, border-color 120ms ease',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase'}}
                                >
                                    Entity Type
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {entityTypeGroup.chips.map((chip) => {
                                        const selected = isFacetChipSelected(entityFilters, chip);
                                        const meta = getEntityTypeMeta(chip.token);
                                        const dotColor = meta.color || '#9e9e9e';
                                        return (
                                            <Chip
                                                key={chip.id}
                                                data-testid={`chart-facet-chip-${chip.id}`}
                                                label={
                                                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                                                        <Box component="span" sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0, display: 'inline-block'}}/>
                                                        {chip.label}
                                                        <span style={{fontSize: 11, fontWeight: 700, lineHeight: 1, opacity: selected ? 0.75 : 0.5}}>
                                                            {chip.count}
                                                        </span>
                                                    </span>
                                                }
                                                variant={selected ? 'filled' : 'outlined'}
                                                color={selected ? 'primary' : 'default'}
                                                onClick={() => handleChipClick(chip)}
                                                aria-pressed={selected}
                                                sx={{fontSize: 13, cursor: 'pointer', fontWeight: selected ? 700 : 500}}
                                            />
                                        );
                                    })}
                                </Stack>
                            </Box>
                        )}
                        {/* Thicker divider between entity type and property groups */}
                        {entityTypeGroup && propertyGroups.length > 0 && (
                            <Divider orientation="vertical" flexItem sx={{alignSelf: 'stretch', mx: 0.5, borderRightWidth: 2}}/>
                        )}
                        {propertyGroups.map((group) => (
                            <Box
                                key={group.groupLabel}
                                onMouseEnter={() => setHoveredGroup(group.groupLabel)}
                                onMouseLeave={() => setHoveredGroup(null)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: hoveredGroup === group.groupLabel ? 'divider' : 'transparent',
                                    bgcolor: hoveredGroup === group.groupLabel ? 'action.hover' : 'transparent',
                                    transition: 'background-color 120ms ease, border-color 120ms ease',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase'}}
                                >
                                    {group.groupLabel}
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {group.chips.map((chip) => {
                                        const selected = isFacetChipSelected(entityFilters, chip);
                                        const avatarUrl = chip.valueEntityId
                                            ? `/api/entities/wikidata/${chip.valueEntityId}/avatar`
                                            : null;
                                        const tooltipTitle = `${chip.groupLabel}: ${chip.label}${chip.count > 1 ? ` · ${chip.count} ${chip.count === 1 ? 'entity' : 'entities'}` : ' · 1 entity'}`;
                                        const chipLabel = (
                                            <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                                                {avatarUrl && (
                                                    <Avatar
                                                        src={avatarUrl}
                                                        alt={chip.label}
                                                        slotProps={{img: {referrerPolicy: 'strict-origin-when-cross-origin'}}}
                                                    sx={{
                                                        width: 18,
                                                        height: 18,
                                                        fontSize: 10,
                                                            bgcolor: 'action.disabledBackground',
                                                            display: 'inline-flex',
                                                            flexShrink: 0,
                                                            ml: '-2px !important',
                                                            mr: '0px !important',
                                                        }}
                                                    >
                                                        {chip.label.slice(0, 1).toUpperCase()}
                                                    </Avatar>
                                                )}
                                                {chip.label}
                                                <span style={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                    opacity: selected ? 0.75 : 0.5,
                                                }}>
                                                    {chip.count}
                                                </span>
                                            </span>
                                        );
                                        return (
                                            <Tooltip
                                                key={chip.id}
                                                title={tooltipTitle}
                                                placement="top"
                                                enterDelay={400}
                                                enterNextDelay={400}
                                                arrow
                                            >
                                                <Chip
                                                    data-testid={`chart-facet-chip-${chip.id}`}
                                                    label={chipLabel}
                                                    variant={selected ? 'filled' : 'outlined'}
                                                    color={selected ? 'primary' : 'default'}
                                                    onClick={() => handleChipClick(chip)}
                                                    aria-pressed={selected}
                                                    sx={{
                                                        fontSize: 13,
                                                        cursor: 'pointer',
                                                        fontWeight: selected ? 700 : 500,
                                                        pl: avatarUrl ? 0.5 : undefined,
                                                    }}
                                                />
                                            </Tooltip>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Stack>
            </Collapse>
        </Box>
    );
}
